import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Modal } from 'react-bootstrap'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from "react-datepicker"

import DeleteModal from 'pages/parts/DeleteModal'
import FilterSearch from 'pages/parts/FilterSearch'
import PeriodPicker from 'pages/parts/PeriodPicker'
import { httpOk, GET, POST, PUT, DELETE, FILE_D } from 'helpers/api'
import { formatMoney, returnSign, formatDate, formatMoneyInput, copyText, returnStatusClass, buildCashAccountSelectOptions, getAccountSelectProps, pickCashAccountId, findKeyFromArrayByValue } from "helpers/helpers"
import { renderSortIndicator, patchSortFilterOnColumnClick } from 'helpers/tableSortIndicator'

function sortSalaryRows(rows, orderBy, sort) {
	if (!orderBy || !Array.isArray(rows)) return rows ?? []

	const dir = sort === 'desc' ? -1 : 1

	return [...rows].sort((a, b) => {
		if (orderBy === 'first_name' || orderBy === 'position') {
			const av = String(a?.[orderBy] ?? '').toLowerCase()
			const bv = String(b?.[orderBy] ?? '').toLowerCase()
			return av.localeCompare(bv, undefined, { sensitivity: 'base' }) * dir
		}

		if (orderBy === 'total_amount') {
			const av = Number(a?.[orderBy]) || 0
			const bv = Number(b?.[orderBy]) || 0
			return (av - bv) * dir
		}

		return 0
	})
}

function getBalanceEndClass(balanceEnd) {
	const value = Number(balanceEnd) || 0
	// float-хвост (−0.04) не считать переплатой — как 0
	if (Math.abs(value) < 0.01) return 'text-success'
	if (value > 0) return 'text-danger' // остаток к выплате
	if (value < 0) return 'text-success' // излишние оплаты
	return 'text-success'
}

/** Остаток: к выплате — как есть; переплата — без минуса (цвет говорит смысл). */
function formatBalanceEnd(balanceEnd) {
	const value = Number(balanceEnd) || 0
	if (Math.abs(value) < 0.01) return formatMoney(0)
	return formatMoney(Math.abs(value))
}

function getDefaultFilterData(posId) {
	const now = new Date()

	return {
		'start_date': formatDate(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd'),
		'end_date': formatDate(now, 'yyyy-MM-dd'),
		'pos_id': posId,
		'status': 1,
		'order_by': 'first_name',
		'sort': 'asc',
	}
}

function Index() {
	const { t } = useTranslation()
	const tableScrollRef = useRef(null)

	const reduxAccount = useSelector(state => state.account)

	const statusTypes = [
		{ 'id': 1, 'name': t('works') },
		{ 'id': -2, 'name': t('fired') }
	]

	const salaryTypes = [
		{ 'id': 1, 'name': t('schedule') },
		{ 'id': 2, 'name': t('hourly') },
		{ 'id': 3, 'name': t('salary') },
		{ 'id': 4, 'name': t('motivation_program') },
		{ 'id': 5, 'name': t('arbitary') },
	]
	const [data, setData] = useState({ 'data': [] })
	const [employeeSearch, setEmployeeSearch] = useState('')
	const [ppt, setPPT] = useState([])
	const [poses, setPoses] = useState([])
	const [userRoles, setUserRoles] = useState([])
	const [employeePositions, setEmployeePositions] = useState([])
	const [accounts, setAccounts] = useState([])
	const [modalPayment, setModalPayment] = useState(false)
	const [modalInfo, setModalInfo] = useState(false)
	const [modalSalesDetails, setModalSalesDetails] = useState(false)
	const [selectedItem, setSelectedItem] = useState({})
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterData, setFilterData] = useState(() => getDefaultFilterData(reduxAccount?.pos_id))
	const [modalDelete, setModalDelete] = useState(false)
	const [deleteModalParams, setDeleteModalParams] = useState({ 'id': 0, 'index': 0 })

	function buildFilterParams(filterDataCopy = {}) {
		var urlParams = ""

		if (filterDataCopy.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterDataCopy.pos_id
		if (filterDataCopy.status)
			urlParams += returnSign(urlParams) + 'status=' + filterDataCopy.status
		if (filterDataCopy.role)
			urlParams += returnSign(urlParams) + 'role=' + filterDataCopy.role
		if (filterData.employee_position_ids && filterDataCopy.employee_position_ids.length > 0) {
			const ids = filterDataCopy.employee_position_ids.map(item => item.id).join(',');
			urlParams += returnSign(urlParams) + 'employee_position_ids=' + ids;
		}
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date

		return urlParams
	}

	function handleTableSort(fieldKey) {
		setFilterData((prev) => patchSortFilterOnColumnClick(prev, fieldKey))
	}

	function tableSortState() {
		return {
			order_by: filterData.order_by ?? null,
			sort: filterData.sort === 'desc' ? 'desc' : 'asc',
		}
	}

	// Бренд может не вести перенос остатка по зарплате — тогда колонки «Остаток на начало» нет,
	// а «Остаток на конец» = начислено − выплачено за выбранный период
	const showBalanceStart = data?.is_salary_saldo_enabled !== false
	// Слагаемое «Остаток на начало» в формуле карточки сотрудника — только когда сальдо ведётся
	const balanceStartTerm = showBalanceStart ? `${t('remaining_start')} + ` : ''

	const sortedRows = useMemo(() => {
		const query = employeeSearch.trim().toLowerCase()
		const rows = !query
			? (data?.data ?? [])
			: (data?.data ?? []).filter((item) => {
				const name = String(item?.first_name ?? '').toLowerCase()
				const login = String(item?.login ?? '').toLowerCase()
				return name.includes(query) || login.includes(query)
			})
		return sortSalaryRows(rows, filterData.order_by, filterData.sort)
	}, [data?.data, filterData.order_by, filterData.sort, employeeSearch])

	async function searchWithFilter(filterParams = { tableSort: "", passedFilterData: {} }) {
		var filterDataCopy = { ...filterData }
		if (filterParams.passedFilterData && Object.keys(filterParams.passedFilterData).length > 0) {
			filterDataCopy = filterParams.passedFilterData
		}

		var filterUrl = "/employee-salary"
		const urlParams = buildFilterParams(filterDataCopy)
		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setDropdownIsOpen(false)
			return
		}
		// Ошибка уже показана toast из api.js — фильтр не применяем, данные не меняем
	}

	async function downloadExcelFile() {
		let urlParams = buildFilterParams({ ...filterData })
		urlParams += returnSign(urlParams) + 'excel=true'
		const response = await FILE_D(`/employee-salary${urlParams}`, { loader: true })
		if (httpOk(response)) {
			const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
			const fileUrl = URL.createObjectURL(file)
			const link = document.createElement('a')
			link.href = fileUrl
			link.setAttribute('download', `salary.xlsx`)
			document.body.appendChild(link)
			link.click()
			URL.revokeObjectURL(fileUrl)
		}
	}

	async function createPayment(e) {
		e.preventDefault();
		var selectedItemCopy = { ...selectedItem }

		var response
		selectedItemCopy.created_at = formatDate(selectedItemCopy.created_at, 'yyyy-MM-dd')
		if (selectedItem.first_name) {
			response = await POST('/employee-payment', selectedItemCopy, { loader: true })
		} else {
			response = await PUT('/employee-payment', selectedItemCopy, { loader: true })
		}
		if (httpOk(response)) {
			searchWithFilter()
			setModalPayment(false)
		}
	}

	function toggleModalPayment(item) {
		var selectedItemCopy = { ...item }
		selectedItemCopy.amount = ''
		selectedItemCopy.note = ''
		selectedItemCopy.created_at = new Date()

		setSelectedItem(selectedItemCopy)
		setModalPayment(true)
	}

	async function deleteItem() {
		const response = await DELETE(`/employees-payment/${deleteModalParams.id}`, {}, { loader: true })
		if (httpOk(response)) {
			searchWithFilter()
			setModalInfo(false)
			setModalDelete(false)
		}
	}

	async function toggleModalInfo(item) {
		const response = await GET('/employee-detail', {
			'login': item.login,
			'pos_id': filterData.pos_id,
			'start_date': filterData.start_date,
			'end_date': filterData.end_date,
		}, { loader: true })

		if (httpOk(response)) {
			setSelectedItem({
				...item,
				'payments': response.data.data,
				'total_payments': response.data.total_payments,
			})

			setModalInfo(true)
		}
	}

	async function toggleModalDetailSales(bool = false, item = {}) {
		if (!bool) {
			setModalSalesDetails(false)
			return
		}

		setSelectedItem({
			...item,
			salesDetail: null,
		})
		setModalSalesDetails(true)

		const response = await POST('/employee-detail-sales', {
			'employee_id': item.id,
			'employee_login': item.login,
			'pos_id': filterData.pos_id,
			'start_date': filterData.start_date,
			'end_date': filterData.end_date,
		}, { loader: true })

		if (httpOk(response)) {
			setSelectedItem((prev) => ({
				...prev,
				salesDetail: response.data,
			}))
		}
	}

	/** Подсказка для колонки "Оплата" — текст обрезается многоточием */
	function getSalaryTypeTitle(item) {
		if (item.is_time_rate_salary)
			return `${t('time_rates')}: ${item.time_rate_name || ''} — ${formatMoney(item.time_rate_value)}`
		return salaryTypes.find(e => e.id === item.salary_type_id)?.['name'] || ''
	}

	function resetFilter() {
		const defaultFilterData = getDefaultFilterData(reduxAccount?.pos_id)
		setFilterData(defaultFilterData)
		setEmployeeSearch('')
		searchWithFilter({ passedFilterData: defaultFilterData })
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	async function getAccounts() {
		const response = await GET('/helper/accounts')
		if (httpOk(response)) setAccounts(response.data)
	}

	async function getPPT() {
		const response = await GET('/helper/ppt-expense?counterparty=employee_salary')
		if (httpOk(response)) setPPT(response.data)
	}

	async function getUserRoles() {
		const response = await GET('/helper/user-roles')
		if (httpOk(response)) setUserRoles(response.data)
	}

	async function getEmployeePositions() {
		const response = await GET(`/helper/employee-positions`)
		if (httpOk(response)) setEmployeePositions(response.data)
	}

	async function getPoses() {
		const response = await GET('/helper/poses-head')
		if (httpOk(response)) setPoses(response.data)
	}

	useEffect(() => {
		searchWithFilter()
		getPoses()
		getPPT()
		getAccounts()
		getUserRoles()
		getEmployeePositions()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// Прокрутка таблицы перетаскиванием мыши + смещение примёрзшей колонки с именем
	useEffect(() => {
		const el = tableScrollRef.current
		if (!el) return

		// ширина первой колонки (#) → левое смещение примёрзшей колонки с именем
		function syncStickyOffset() {
			const firstTh = el.querySelector('thead th')
			if (firstTh) el.style.setProperty('--col1-w', firstTh.offsetWidth + 'px')
		}
		syncStickyOffset()
		window.addEventListener('resize', syncStickyOffset)

		let isDown = false
		let moved = false
		let startX = 0
		let startLeft = 0

		function onMouseDown(e) {
			if (e.button !== 0) return
			moved = false
			// не мешаем кликам по кнопкам/ссылкам/полям
			if (e.target.closest('button, a, input, select, .table-action-button')) return
			isDown = true
			startX = e.pageX
			startLeft = el.scrollLeft
		}
		function onMouseMove(e) {
			if (!isDown) return
			const dx = e.pageX - startX
			if (!moved && Math.abs(dx) < 5) return
			moved = true
			el.classList.add('is-dragging')
			el.scrollLeft = startLeft - dx
			e.preventDefault()
		}
		function onMouseUp() {
			isDown = false
			el.classList.remove('is-dragging')
		}
		// после перетаскивания гасим клик (иначе сработает сортировка/копирование)
		function onClickCapture(e) {
			if (moved) {
				e.stopPropagation()
				e.preventDefault()
				moved = false
			}
		}

		el.addEventListener('mousedown', onMouseDown)
		window.addEventListener('mousemove', onMouseMove)
		window.addEventListener('mouseup', onMouseUp)
		el.addEventListener('click', onClickCapture, true)

		return () => {
			window.removeEventListener('resize', syncStickyOffset)
			el.removeEventListener('mousedown', onMouseDown)
			window.removeEventListener('mousemove', onMouseMove)
			window.removeEventListener('mouseup', onMouseUp)
			el.removeEventListener('click', onClickCapture, true)
		}
	}, [])

	const accountChefOptions = buildCashAccountSelectOptions(accounts)

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center h-100">
					{t('salary')} [{data?.data?.length}]
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between mb-3">
						<div className="d-flex gap-2">
							<div>
								<FilterSearch
									value={employeeSearch}
									onSearchChange={setEmployeeSearch}
									minLength={0}
									debounceMs={200}>
								</FilterSearch>
							</div>
							<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
									{t('filter')}
								</Dropdown.Toggle>

								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('pos')}</label>
											<Select
												options={poses}
												value={poses.find(option => option.id === filterData.pos_id) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'pos_id': option.id })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
										<div className="form-group col-md-6">
											<label>{t('status')}</label>
											<Select
												options={statusTypes}
												value={statusTypes.find(option => option.id === filterData.status) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'status': option.id })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
									</div>
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('role')}</label>
											<Select
												isClearable
												options={userRoles}
												value={userRoles.find(option => option.role === filterData.role) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'role': option ? option.role : null })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.role}
											/>
										</div>
										<div className="form-group col-md-6">
											<label>{t('positions')}</label>
											<Select
												isMulti
												isClearable
												options={employeePositions}
												value={filterData.employee_position_ids}
												onChange={(options) => setFilterData({ ...filterData, 'employee_position_ids': options ? options : null })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
									</div>
									<div className="row">
										<div className="form-group col-md-12">
											<label>{t('period')}</label>
											<PeriodPicker
												withTime={false}
												startDate={filterData.start_date}
												endDate={filterData.end_date}
												minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
												maxDate={new Date(new Date().getFullYear(), 11, 31)}
												onChange={(period) => setFilterData({ ...filterData, ...period })}
											/>
										</div>
									</div>
									<div className="row mt-3">
										<div className="col-md-12">
											<div className="d-flex justify-content-end gap-2">
												<button className="btn btn-secondary"
													onClick={() => setDropdownIsOpen(false)}>
													{t('cancel')}
												</button>
												<button className="btn btn-primary"
													onClick={() => searchWithFilter()}>
													{t('apply')}
												</button>
											</div>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>
							<div>
								<button className="btn btn-outline-primary btn-wide-2"
									title="EXCEL"
									onClick={() => downloadExcelFile()}>
									<i className="uil uil-file-download-alt"></i>
								</button>
							</div>
						</div>
						<div className="d-flex gap-2">
							<div className="vertical-center">
								<h4><b>{t('salary')} + KPI</b>:</h4>
							</div>
							<div className="vertical-center text-end">
								<h4>{formatMoney(data.overall_bonus_and_salary)}</h4>
							</div>
						</div>
					</div>

					<div className="filter-block">
						<div className="filter">
							<strong>{t('filter')}</strong>
							<br />
							{(filterData.start_date || filterData.end_date) &&
								<span className="me-2">
									<b>{t('period')}: </b>
									{filterData.start_date &&
										<span>{formatDate(filterData.start_date)}</span>
									}
									{(filterData.start_date && filterData.end_date) &&
										<b> - </b>
									}
									{filterData.end_date &&
										<span>{formatDate(filterData.end_date)}</span>
									}
								</span>
							}
							{!!filterData.pos_id &&
								<span className="me-2">
									<b>{t('pos')}: </b>
									<span>{findKeyFromArrayByValue(poses, 'id', filterData.pos_id, 'name')}</span>
								</span>
							}
							{!!filterData.status &&
								<span className="me-2">
									<b>{t('status')}: </b>
									<span>{findKeyFromArrayByValue(statusTypes, 'id', filterData.status, 'name')}</span>
								</span>
							}
							{!!filterData.role &&
								<span className="me-2">
									<b>{t('role')}: </b>
									<span>{findKeyFromArrayByValue(userRoles, 'role', filterData.role, 'name')}</span>
								</span>
							}
							{filterData.employee_position_ids?.length > 0 &&
								<span className="me-2">
									<b>{t('positions')}: </b>
									<span>{filterData.employee_position_ids.map(item => item.name).join(', ')}</span>
								</span>
							}
							{employeeSearch.trim() &&
								<span className="me-2">
									<b>{t('search')}: </b>
									<span>{employeeSearch.trim()}</span>
								</span>
							}
						</div>
						<div className="vertical-center">
							<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
						</div>
					</div>

					<div className="table-responsive drag-scroll" ref={tableScrollRef}>
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ 'width': '33px' }} className="text-center">#</th>
									<th
										style={{ 'width': '160px' }}
										className="text-nowrap th-sortable user-select-none"
										onClick={() => handleTableSort('first_name')}
										title={t('sort_by')}>
										<div className="d-flex align-items-center justify-content-between gap-2 pe-1">
											<span>{t('employee')}</span>
											{renderSortIndicator('first_name', tableSortState())}
										</div>
									</th>
									<th
										className="text-nowrap th-sortable user-select-none"
										onClick={() => handleTableSort('position')}
										title={t('sort_by')}>
										<div className="d-flex align-items-center justify-content-between gap-2 pe-1">
											<span>{t('positions')}</span>
											{renderSortIndicator('position', tableSortState())}
										</div>
									</th>
									<th className="text-nowrap" style={{ minWidth: '110px' }}>{t('payment')}</th>
									{showBalanceStart &&
										<th
											className="text-end text-nowrap"
											style={{ minWidth: '100px' }}
											title={t('remaining_start')}>
											{t('remaining_start')}
										</th>
									}
									<th className="text-end">{t('salary')}</th>
									<th className="text-end">{t('days')}</th>
									<th className="text-end">{t('hours')}</th>
									<th className="text-end">{t('sales')}</th>
									<th className="text-end">KPI</th>
									<th
										className="text-end text-nowrap"
										style={{ minWidth: '90px' }}
										title={t('accrued_bonus')}>
										{t('accrued_bonus')}
									</th>
									<th
										className="text-end text-nowrap"
										style={{ minWidth: '72px' }}
										title={t('fines')}>
										{t('fines')}
									</th>
									{data.has_tax_withheld &&
										<th
											className="text-end text-nowrap"
											style={{ minWidth: '96px' }}
											title={t('tax_withheld')}>
											{t('tax_withheld')}
										</th>
									}
									{data.has_tax_employer &&
										<th
											className="text-end text-nowrap"
											style={{ minWidth: '96px' }}
											title={t('tax_employer')}>
											{t('tax_employer')}
										</th>
									}
									<th
										className="text-end text-nowrap th-sortable user-select-none"
										style={{ minWidth: '110px' }}
										title={t('general_payments')}
										onClick={() => handleTableSort('total_amount')}
									>
										<div className="d-flex align-items-center justify-content-end gap-1">
											<span>{t('general_payments')}</span>
											{renderSortIndicator('total_amount', tableSortState())}
										</div>
									</th>
									<th
										className="text-end text-nowrap"
										style={{ minWidth: '100px' }}
										title={t('remaining_end')}>
										{t('remaining_end')}
									</th>
									<th style={{ minWidth: '96px' }}></th>
								</tr>
							</thead>
							<tbody>
								{sortedRows?.map((item, index) => (
									<tr key={index} className={`cursor ${selectedItem.id === item.id ? 'table-tr-active' : ''}`}>
										<td className="text-center">{index + 1}</td>
										<td className="td-ellipsis"
											title={item.first_name}
											onClick={() => copyText(item.login)}>
											{item.first_name}
										</td>
										<td className="td-ellipsis" title={item.position}>{item.position}</td>
										<td className="td-ellipsis" title={getSalaryTypeTitle(item)}>
											{/* оплата по времени перебивает тип оплаты должности */}
											{item.is_time_rate_salary &&
												<span>{t('time_rates')}</span>
											}
											{!item.is_time_rate_salary && item.salary_type_id &&
												<span>{salaryTypes.find(e => e.id === item.salary_type_id)?.['name']}</span>
											}
										</td>
										{showBalanceStart &&
											<td className="text-nowrap text-end">
												<span
													className={getBalanceEndClass(item.balance_start)}
													title={t('remaining_start')}>
													{formatBalanceEnd(item.balance_start)}
												</span>
											</td>
										}
										<td className="text-nowrap text-end">{formatMoney(item.salary)}</td>
										<td className="text-nowrap text-end">{item.working_days ? item.working_days : 0}</td>
										<td className="text-nowrap text-end">{item.working_time_formatted ? item.working_time_formatted : 0}</td>
										<td className="text-nowrap text-end">{formatMoney(item?.sales)}</td>
										<td className="text-nowrap text-end">{formatMoney(item.bonus)}</td>
										<td className="text-end">{formatMoney(item.total_accrued_bonus)}</td>
										<td className="text-end">{formatMoney(item.total_fines)}</td>
										{data.has_tax_withheld &&
											<td className="text-nowrap text-end">{formatMoney(item.tax_withheld)}</td>
										}
										{data.has_tax_employer &&
											<td className="text-nowrap text-end">{formatMoney(item.tax_employer)}</td>
										}
										<td className="text-nowrap text-end">{formatMoney(item.total_amount)}</td>
										<td className="text-nowrap text-end">
											<span
												className={getBalanceEndClass(item.balance_end)}
												title={
													Number(item.balance_end) > 0
														? 'Ещё должны сотруднику'
														: Number(item.balance_end) < 0
															? 'Переплата'
															: 'Рассчитан'
												}>
												{formatBalanceEnd(item.balance_end)}
											</span>
										</td>
										<td>
											<div className="d-flex justify-content-center gap-1">
												<button type="button" className="table-action-button table-action-primary-button"
													title={t('payment')}
													onClick={() => toggleModalPayment(item)}>
													<i className="uil-transaction"></i>
												</button>
												<button type="button" className="table-action-button table-action-primary-button"
													title={t('history')}
													onClick={() => toggleModalInfo(item)}>
													<i className="uil-history"></i>
												</button>
												<button type="button" className="table-action-button table-action-primary-button"
													title={t('information')}
													onClick={() => toggleModalDetailSales(true, item)}>
													<i className="uil-info"></i>
												</button>
											</div>
										</td>
									</tr>
								))}
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td></td>
									<td></td>
									{showBalanceStart &&
										<td className={`text-end text-nowrap ${getBalanceEndClass(data.overall_balance_start)}`}>
											<b>{formatBalanceEnd(data.overall_balance_start)}</b>
										</td>
									}
									<td className="text-end text-nowrap"><b>{formatMoney(data.overall_salary)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.overall_working_days)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.overall_working_hours, 2)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.overall_sales)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.overall_bonus)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.overall_total_accrued_bonus)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.overall_total_fines)}</b></td>
									{data.has_tax_withheld &&
										<td className="text-end text-nowrap"><b>{formatMoney(data.overall_tax_withheld)}</b></td>
									}
									{data.has_tax_employer &&
										<td className="text-end text-nowrap"><b>{formatMoney(data.overall_tax_employer)}</b></td>
									}
									<td className="text-end text-nowrap">
										<b className="text-success">{formatMoney(data.overall_total_amount)}</b>
									</td>
									<td className={`text-end text-nowrap ${getBalanceEndClass(data.overall_balance_end)}`}>
										<b>{formatBalanceEnd(data.overall_balance_end)}</b>
									</td>
									<td></td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* MODAL PAYMENT*/}
			<Modal
				show={modalPayment}
				animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => setModalPayment(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						<span>{selectedItem.first_name}</span>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={createPayment} autoComplete="off">
						<div className="form-group">
							<label>{t('date')}</label>
							<div className="calendar-input">
								<DatePicker
									showMonthDropdown
									showYearDropdown
									dropdownMode="select"
									yearDropdownItemNumber={2}
									disabled={selectedItem.update}
									className="form-control"
									dateFormat="dd.MM.yyyy"
									selected={selectedItem.created_at ? new Date(selectedItem.created_at) : ''}
									onChange={(date) => setSelectedItem({ ...selectedItem, 'created_at': formatDate(date, 'yyyy-MM-dd') })}
									minDate={new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)}
									maxDate={new Date()}
								/>
								<i className="uil uil-calendar-alt"></i>
							</div>
						</div>
						<>
							<div className="form-group">
								<label>{t('expense_type')}<span className="required-mark">*</span></label>
								<Select
									isDisabled={selectedItem.update}
									options={ppt}
									value={ppt.find(option => option.id === selectedItem.payment_purpose_id)}
									onChange={(option) => {
										const legacyNoAccount = ['neutral', 'neutral_plus'].includes(option.transaction_type)
										const accountRequired = option.is_account_required !== false && !legacyNoAccount
										setSelectedItem({
											...selectedItem,
											'payment_purpose_id': option.id,
											'account_chef_id': accountRequired ? pickCashAccountId(accounts, option.account_chef_id) : null,
											'account_core_id': option.account_core_id,
											'is_account_required': accountRequired,
											'transaction_type': option.transaction_type,
										})
									}}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>
							{(selectedItem.is_account_required !== false) &&
								<>
									<div className="form-group">
										<label>{t('operation_account')}<span className="required-mark">*</span></label>
										<Select
											isClearable
											{...getAccountSelectProps(t)}
											options={accountChefOptions}
											value={accountChefOptions.find(option => option.id === selectedItem.account_chef_id) || null}
											onChange={(option) => setSelectedItem({ ...selectedItem, 'account_chef_id': option ? option.id : null })}
										/>
									</div>
									<div className="form-group d-none">
										<label>{t('report_account')}</label>
										<Select
											isDisabled
											options={accounts}
											value={accounts.find(option => option.id === selectedItem.account_core_id)}
											onChange={(option) => setSelectedItem({ ...selectedItem, 'account_core_id': option ? option.id : null })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
								</>
							}
						</>
						<div className="form-group">
							<label>{t('amount')}<span className="required-mark">*</span></label>
							<div className="position-relative">
								<input type="text" className="form-control" name="amount"
									value={selectedItem.amount ? formatMoneyInput(selectedItem.amount) : ''}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'amount': e.target.value.replace(/[^0-9.]/g, '') })} />
								<i className="input-inner-icon uil uil-transaction"
									style={{ 'right': '10px', 'top': '10px' }}
									onClick={() => setSelectedItem({
										...selectedItem,
										'amount': Math.max(0, Number(selectedItem.balance_end) || 0).toFixed(0)
									})}></i>
							</div>
						</div>
						<div className="form-group">
							<label>{t('note')}</label>
							<input type="text" className="form-control" name="note"
								value={selectedItem.note}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'note': e.target.value })} />
						</div>
						<div className="d-flex w-100 mt-3">
							<button type="button" className="btn btn-outline-warning w-100 me-2" onClick={() => setModalPayment(false)}>{t('cancel')}</button>
							<button type="submit" className="btn btn-primary w-100">{t('save')}</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL PAYMENT*/}

			{/* MODAL INFO*/}
			<Modal show={modalInfo} animation={false} centered size="xl"
				dialogClassName="update-modal-width"
				onHide={() => setModalInfo(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{selectedItem.first_name}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="table-responsive table-fixed" style={{ 'height': '500px', 'overflow': 'auto' }}>
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'minWidth': '115px' }} className="text-center text-nowrap">{t('date')}</th>
									<th style={{ 'width': '15%' }} className="text-start text-nowrap">{t('type')}</th>
									<th style={{ 'width': '15%' }} className="text-start text-nowrap">{t('operation_account')}</th>
									<th style={{ 'width': '25%' }} className="text-start text-nowrap">{t('note')}</th>
									<th style={{ 'width': '15%' }} className="text-start text-nowrap">{t('created_by')}</th>
									<th style={{ 'width': '15%' }} className="text-center text-nowrap">{t('status')}</th>
									<th style={{ 'width': '15%' }} className="text-end text-nowrap">{t('amount')}</th>
									<th style={{ 'minWidth': '33px' }} className="text-end"></th>
								</tr>
							</thead>
							<tbody>
								{selectedItem?.payments?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{index + 1}</td>
										<td className="text-center">{formatDate(item.created_at, 'dd.MM.yy HH:mm')}</td>
										<td className="td-ellipsis" title={item.name}>{item.name}</td>
										<td className="td-ellipsis" title={item.account_chef_name || ''}>
											{item.account_chef_name || '—'}
										</td>
										<td className="td-ellipsis" title={item.note}>{item.note}</td>
										<td className="td-ellipsis" title={item.item}>{item.created_by}</td>
										<td className={`td-ellipsis text-center ${returnStatusClass(item.status)}`}>
											{item.status === -1 ?
												<span>{t('deleted')}</span>
												:
												<span>{t('success')}</span>
											}
										</td>
										<td className="text-end text-nowrap">{formatMoney(item.amount)}</td>
										<td>
											{item.status !== -1 &&
												<div className="d-flex justify-content-end">
													<div className="table-action-button table-action-danger-button"
														onClick={() => {
															setDeleteModalParams({ 'id': item.id, 'index': index });
															setModalDelete(true)
														}}>
														<i className="uil-times"></i>
													</div>
												</div>
											}
										</td>
									</tr>
								))}
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td></td>
									<td></td>
									<td></td>
									<td></td>
									<td></td>
									<td className="text-end text-nowrap">
										<b>{formatMoney(selectedItem?.total_payments)}</b>
									</td>
									<td></td>
								</tr>
							</tbody>
						</table>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL INFO*/}

			{/* MODAL SALARY BREAKDOWN */}
			<Modal show={modalSalesDetails} animation={false} centered size="lg"
				dialogClassName="update-modal-width"
				onHide={() => toggleModalDetailSales(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{selectedItem?.first_name || selectedItem?.salesDetail?.cheque?.employee_name}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="table-responsive mb-3">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th colSpan={2}>{t('information')}</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td style={{ width: '45%' }}>{t('salary_type')}</td>
									<td className="text-end">
										{selectedItem?.is_time_rate_salary
											? `${t('time_rates')}: ${selectedItem?.time_rate_name || ''}`
											: selectedItem?.salary_type_id
												? (salaryTypes.find((e) => e.id === selectedItem.salary_type_id)?.name || '—')
												: 'KPI'}
									</td>
								</tr>
								{selectedItem?.is_time_rate_salary &&
									<tr>
										<td>{t('rate_amount')}</td>
										<td className="text-end">{formatMoney(selectedItem?.time_rate_value)}</td>
									</tr>
								}
								{(selectedItem?.is_time_rate_salary ||
									selectedItem?.salary_type_id === 1 ||
									selectedItem?.salary_type_id === 2 ||
									selectedItem?.salary_type_id === 3) &&
									<tr>
										<td>{t('salary')}</td>
										<td className="text-end">{formatMoney(selectedItem?.salary)}</td>
									</tr>
								}
								<tr>
									<td>{t('days')}</td>
									<td className="text-end">{selectedItem?.working_days || 0}</td>
								</tr>
								<tr>
									<td>{t('hours')}</td>
									<td className="text-end">{selectedItem?.working_time_formatted || formatMoney(selectedItem?.working_hours, 2)}</td>
								</tr>
								<tr>
									<td>{t('sales')}</td>
									<td className="text-end">{formatMoney(selectedItem?.sales)}</td>
								</tr>
								<tr>
									<td>KPI / {t('bonus')}</td>
									<td className="text-end">{formatMoney(selectedItem?.bonus)}</td>
								</tr>
								<tr>
									<td>{t('accrued_bonus')}</td>
									<td className="text-end">{formatMoney(selectedItem?.total_accrued_bonus)}</td>
								</tr>
								<tr>
									<td>{t('fines')}</td>
									<td className="text-end text-danger">{formatMoney(selectedItem?.total_fines)}</td>
								</tr>
								{Number(selectedItem?.tax_withheld) > 0 &&
									<tr>
										<td>{t('tax_withheld')}</td>
										<td className="text-end text-danger">
											{formatMoney(selectedItem?.tax_withheld)}
											{Number(selectedItem?.tax_income) > 0 && Number(selectedItem?.tax_pension) > 0 &&
												<small className="text-muted ms-2">
													{t('income_tax_rate').replace(', %', '')} {formatMoney(selectedItem?.tax_income)} · {t('pension_rate').replace(', %', '')} {formatMoney(selectedItem?.tax_pension)}
												</small>
											}
										</td>
									</tr>
								}
								{Number(selectedItem?.tax_employer) > 0 &&
									<tr>
										<td>{t('tax_employer')}</td>
										<td className="text-end">{formatMoney(selectedItem?.tax_employer)}</td>
									</tr>
								}
								<tr>
									<td>{t('general_payments')}</td>
									<td className="text-end text-success">{formatMoney(selectedItem?.total_amount)}</td>
								</tr>
								{showBalanceStart &&
									<tr>
										<td>{t('remaining_start')}</td>
										<td className={`text-end ${getBalanceEndClass(selectedItem?.balance_start)}`}>
											{formatBalanceEnd(selectedItem?.balance_start)}
										</td>
									</tr>
								}
								<tr>
									<td><b>{t('remaining_end')}</b></td>
									<td className={`text-end ${getBalanceEndClass(selectedItem?.balance_end)}`}>
										<b>{formatBalanceEnd(selectedItem?.balance_end)}</b>
									</td>
								</tr>
							</tbody>
						</table>
						<small className="text-muted d-block mt-2">
							{selectedItem?.is_time_rate_salary
								? `${balanceStartTerm}${t('salary')} + KPI/${t('bonus')} + ${t('accrued_bonus')} − ${t('general_payments')} − ${t('fines')}${Number(selectedItem?.tax_withheld) > 0 ? ` − ${t('tax_withheld')}` : ''} = ${t('remaining_end')}`
								: (selectedItem?.salary_type_id === 1 || selectedItem?.salary_type_id === 2 || selectedItem?.salary_type_id === 3)
									? `${balanceStartTerm}${t('salary')} + ${t('accrued_bonus')} − ${t('general_payments')} − ${t('fines')}${Number(selectedItem?.tax_withheld) > 0 ? ` − ${t('tax_withheld')}` : ''} = ${t('remaining_end')}`
									: `${balanceStartTerm}KPI/${t('bonus')} + ${t('accrued_bonus')} − ${t('general_payments')} − ${t('fines')}${Number(selectedItem?.tax_withheld) > 0 ? ` − ${t('tax_withheld')}` : ''} = ${t('remaining_end')}`
							}
						</small>
					</div>

					{selectedItem?.salesDetail?.cheque &&
						<div className="table-responsive mb-3" style={{ 'minHeight': 0 }}>
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
										<th style={{ 'width': '10%' }} className="td-ellipsis">{t('guests')}</th>
										<th style={{ 'width': '10%' }} className="td-ellipsis">{t('cheques')}</th>
										<th style={{ 'width': '15%' }} className="td-ellipsis text-end">{t('average_check')}</th>
										<th style={{ 'width': '15%' }} className="td-ellipsis text-end">{t('service')}</th>
										<th style={{ 'width': '15%' }} className="td-ellipsis text-end">{t('sales')}</th>
										<th style={{ 'width': '15%' }} className="td-ellipsis text-end">{t('amount')}</th>
										<th style={{ 'width': '20%' }} className="td-ellipsis text-end">KPI</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className="text-center">1</td>
										<td>{formatMoney(selectedItem?.salesDetail?.cheque?.people_number)}</td>
										<td>{formatMoney(selectedItem?.salesDetail?.cheque?.count_cheques)}</td>
										<td className="text-end">{formatMoney(selectedItem?.salesDetail?.cheque?.avg_total_amount)}</td>
										<td className="text-end">{formatMoney(selectedItem?.salesDetail?.cheque?.service_amount)}</td>
										<td className="text-end">{formatMoney(selectedItem?.salesDetail?.cheque?.total_amount)}</td>
										<td className="text-end">{formatMoney(selectedItem?.salesDetail?.cheque?.total_amount_service)}</td>
										<td className="text-end">{formatMoney(selectedItem?.salesDetail?.cheque?.kpi)}</td>
									</tr>
								</tbody>
							</table>
						</div>
					}

					{selectedItem?.salesDetail?.cheque_products?.length > 0 &&
						<div className="table-responsive" style={{ 'height': '400px', 'overflow': 'auto' }}>
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
										<th style={{ 'width': '30%' }} className="td-ellipsis">{t('menu')}</th>
										<th style={{ 'width': '30%' }} className="td-ellipsis">{t('product')}</th>
										<th style={{ 'width': '20%' }} className="td-ellipsis text-end">{t('quantity')}</th>
										<th style={{ 'width': '20%' }} className="td-ellipsis text-end">{t('amount')}</th>
									</tr>
								</thead>
								<tbody>
									{selectedItem.salesDetail.cheque_products.map((item, index) => (
										<tr key={index}>
											<td className="text-center">{index + 1}</td>
											<td className="td-ellipsis">{item.menu_name}</td>
											<td className="td-ellipsis">{item.product_name}</td>
											<td className="text-end">{formatMoney(item.total_quantity)}</td>
											<td className="text-end">{formatMoney(item.total_sales_amount)}</td>
										</tr>
									))}
									<tr>
										<td></td>
										<td><b>{t('total')}</b></td>
										<td></td>
										<td className="text-end"><b>{formatMoney(selectedItem?.salesDetail?.overall_quantity)}</b></td>
										<td className="text-end"><b>{formatMoney(selectedItem?.salesDetail?.overall_sales)}</b></td>
									</tr>
								</tbody>
							</table>
						</div>
					}
				</Modal.Body>
			</Modal>
			{/* MODAL SALARY BREAKDOWN */}

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>
		</>
	)
}

export default Index